import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import folderDocumentsApi, { FolderDocumentDto, ImageConversionResultDto } from '../../services/folderDocumentService';
import { hexToRgba } from '../../utils/colorUtils';
import { handleApiError } from '../../utils/errorHandler';
import ConfirmationModal from './ConfirmationModal';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--background-primary);
  border-radius: var(--border-radius-lg);
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;

  &:hover {
    background: var(--background-primary);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  min-height: 400px;
  background: #ffffff;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: none;
  background: white;
  border-radius: var(--border-radius-md);
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const UnsupportedPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px;
`;

const UnsupportedIcon = styled.div`
  font-size: 72px;
  margin-bottom: 20px;
`;

const UnsupportedTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
`;

const UnsupportedText = styled.p`
  margin: 0 0 24px 0;
  font-size: 15px;
  line-height: 1.6;
  color: #4a4a4a;
`;

const PreviewDownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: transparent;
    color: var(--primary-color);
  }
`;

const PreviewLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconOnlyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
    <path d="M21 3v5h-5"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="17,8 12,3 7,8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7,10 12,15 17,10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const ConvertToPdfIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"/>
    <path d="M14 2V8H20"/>
    <text x="12" y="16" textAnchor="middle" fontSize="6" fontWeight="bold" fill="currentColor" stroke="none">PDF</text>
  </svg>
);

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: none;

    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }
  ` : `
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--background-secondary);
      border-color: var(--primary-color);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const DocumentCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 142, 162, 0.1);
  }
`;

const DocumentIcon = styled.div`
  font-size: 40px;
  text-align: center;
  margin-bottom: 12px;
`;

const DocumentName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  text-align: center;
  word-break: break-word;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--text-secondary);
`;

const NewBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
  background: ${hexToRgba('#16a34a', 0.15)};
  color: #16a34a;
`;

const DocumentActions = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${DocumentCard}:hover & {
    opacity: 1;
  }
`;

const CardIconButton = styled.button<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: ${props => props.danger ? '#dc2626' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.danger ? '#b91c1c' : 'var(--primary-color)'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
`;

const UploadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: ${hexToRgba('#008EA2', 0.1)};
  border: 2px dashed var(--primary-color);
  border-radius: var(--border-radius-lg);
  color: var(--primary-color);
  font-weight: 500;
`;

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface FolderDocumentsGridProps {
  documents: FolderDocumentDto[];
  onUpload: (file: File, notes?: string) => Promise<void>;
  onDelete: (documentId: number) => Promise<void>;
  onDownload: (documentId: number) => void;
  onConvertToPdf?: (documentId: number) => Promise<ImageConversionResultDto>;
  onRefresh: () => void;
  isLoading?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getFileIcon = (extension: string): React.ReactNode => {
  const ext = extension.toLowerCase().replace('.', '');

  // PDF - Red
  if (ext === 'pdf') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#fef2f2"/>
        <path d="M14 2V8H20" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#dc2626">PDF</text>
      </svg>
    );
  }

  // Word - Blue
  if (ext === 'doc' || ext === 'docx') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#eff6ff"/>
        <path d="M14 2V8H20" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#2563eb">DOC</text>
      </svg>
    );
  }

  // Excel - Green
  if (ext === 'xls' || ext === 'xlsx') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#f0fdf4"/>
        <path d="M14 2V8H20" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#16a34a">XLS</text>
      </svg>
    );
  }

  // Images - Purple
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="#7c3aed" strokeWidth="1.5" fill="#faf5ff"/>
        <circle cx="8.5" cy="8.5" r="1.5" fill="#7c3aed"/>
        <path d="M21 15L16 10L5 21" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }

  // Default - Gray document
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#f9fafb"/>
      <path d="M14 2V8H20" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 13H16M8 17H12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
};

const isPreviewableFile = (extension: string): { canPreview: boolean; type: 'pdf' | 'image' | 'unsupported' } => {
  const ext = extension.toLowerCase().replace('.', '');

  if (ext === 'pdf') {
    return { canPreview: true, type: 'pdf' };
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return { canPreview: true, type: 'image' };
  }

  return { canPreview: false, type: 'unsupported' };
};

const getFileTypeName = (extension: string): string => {
  const ext = extension.toLowerCase().replace('.', '');
  switch (ext) {
    case 'doc':
    case 'docx':
      return 'Word';
    case 'xls':
    case 'xlsx':
      return 'Excel';
    case 'ppt':
    case 'pptx':
      return 'PowerPoint';
    default:
      return ext.toUpperCase();
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Allowed file extensions for upload
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.xls', '.xlsx'];
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const MAX_FILE_SIZE_MB = 2;

const isFileTypeAllowed = (fileName: string): boolean => {
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return ALLOWED_EXTENSIONS.includes(ext);
};

const isFileSizeAllowed = (fileSize: number): boolean => {
  return fileSize <= MAX_FILE_SIZE_BYTES;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFormattedAllowedTypes = (): string => {
  return 'PDF, Word, Excel, תמונות (JPG, PNG, GIF, BMP, WebP)';
};

// Check if file is an image that can be converted to PDF
const isConvertibleTooPdf = (extension: string): boolean => {
  const ext = extension.toLowerCase().replace('.', '');
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
};


// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FolderDocumentsGrid: React.FC<FolderDocumentsGridProps> = ({
  documents,
  onUpload,
  onDelete,
  onDownload,
  onConvertToPdf,
  onRefresh,
  isLoading = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<FolderDocumentDto | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
    isLoading?: boolean;
    confirmText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
    onConfirm: () => {},
    isLoading: false,
    confirmText: 'אישור',
  });

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
  });

  const showAlert = (title: string, message: string, variant: 'default' | 'danger' = 'default') => {
    setAlertModal({ isOpen: true, title, message, variant });
  };

  // Clean up preview URL when component unmounts or document changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        // No need to revoke since we're using signed URLs
      }
    };
  }, [previewUrl]);

  // Load preview when document is selected
  const handleDocumentClick = async (doc: FolderDocumentDto) => {
    setSelectedDocument(doc);
    setPreviewUrl(null);

    const previewInfo = isPreviewableFile(doc.fileExtension);

    if (previewInfo.canPreview) {
      setIsLoadingPreview(true);
      try {
        const url = await folderDocumentsApi.getDownloadUrl(doc.id);
        setPreviewUrl(url);
      } catch (error) {
        showAlert('שגיאה', handleApiError(error, 'שגיאה בטעינת התצוגה המקדימה'), 'danger');
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const handleClosePreview = () => {
    setSelectedDocument(null);
    setPreviewUrl(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Validate file types and sizes before upload
    const invalidTypeFiles: string[] = [];
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!isFileTypeAllowed(file.name)) {
        invalidTypeFiles.push(file.name);
      } else if (!isFileSizeAllowed(file.size)) {
        oversizedFiles.push(`${file.name} (${formatFileSize(file.size)})`);
      } else {
        validFiles.push(file);
      }
    }

    // Show error for invalid file types
    if (invalidTypeFiles.length > 0) {
      const fileList = invalidTypeFiles.join(', ');
      showAlert('סוג קובץ לא נתמך', `הקובץ הבא אינו נתמך: ${fileList}\n\nסוגי קבצים נתמכים: ${getFormattedAllowedTypes()}`, 'danger');
    }

    // Show error for oversized files
    if (oversizedFiles.length > 0) {
      const fileList = oversizedFiles.join(', ');
      showAlert('קובץ גדול מדי', `הקובץ הבא חורג מהגודל המקסימלי (${MAX_FILE_SIZE_MB} MB):\n${fileList}`, 'danger');
    }

    // If no valid files, reset and return
    if (validFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    try {
      for (const file of validFiles) {
        await onUpload(file);
      }
    } catch (error) {
      showAlert('שגיאה בהעלאה', handleApiError(error, 'שגיאה בהעלאת הקובץ'), 'danger');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteClick = (documentId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmModal({
      isOpen: true,
      title: 'מחיקת מסמך',
      message: 'האם אתה בטוח שברצונך למחוק את המסמך?',
      variant: 'danger',
      isLoading: false,
      confirmText: 'מחק',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await onDelete(documentId);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          // Close preview if deleted document was being previewed
          if (selectedDocument?.id === documentId) {
            handleClosePreview();
          }
        } catch (error) {
          console.error('Delete error:', error);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          showAlert('שגיאה', 'שגיאה במחיקת המסמך', 'danger');
        }
      },
    });
  };

  const handleConvertToPdfClick = (doc: FolderDocumentDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onConvertToPdf) return;

    setConfirmModal({
      isOpen: true,
      title: 'המרת תמונה ל-PDF',
      message: `האם אתה בטוח שברצונך להמיר את "${doc.originalFileName}" לקובץ PDF?\n\n⚠️ פעולה זו תחליף את הקובץ המקורי ולא ניתן לבטלה.`,
      variant: 'danger',
      isLoading: false,
      confirmText: 'המר ל-PDF',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setIsConverting(true);
        try {
          const result = await onConvertToPdf(doc.id);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setIsConverting(false);
          if (result.success) {
            showAlert('הומר בהצלחה', `המסמך הומר ל-PDF בהצלחה: ${result.newFileName}`);
            // Close preview if converted document was being previewed
            if (selectedDocument?.id === doc.id) {
              handleClosePreview();
            }
            onRefresh();
          } else {
            showAlert('שגיאה בהמרה', result.errorMessage || 'שגיאה לא צפויה בהמרת הקובץ', 'danger');
          }
        } catch (error: any) {
          console.error('Convert error:', error);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setIsConverting(false);
          showAlert('שגיאה', error?.response?.data?.error || 'שגיאה בהמרת המסמך ל-PDF', 'danger');
        }
      },
    });
  };

  // Render preview content based on file type
  const renderPreviewContent = () => {
    if (!selectedDocument) return null;

    const previewInfo = isPreviewableFile(selectedDocument.fileExtension);

    if (isLoadingPreview) {
      return (
        <PreviewLoading>
          <span style={{ fontSize: '40px' }}>⏳</span>
          <span>טוען תצוגה מקדימה...</span>
        </PreviewLoading>
      );
    }

    if (!previewInfo.canPreview) {
      return (
        <UnsupportedPreview>
          <UnsupportedIcon>{getFileIcon(selectedDocument.fileExtension)}</UnsupportedIcon>
          <UnsupportedTitle>לא ניתן להציג תצוגה מקדימה</UnsupportedTitle>
          <UnsupportedText>
            קבצי {getFileTypeName(selectedDocument.fileExtension)} אינם נתמכים לתצוגה מקדימה.
            <br />
            ניתן להוריד את הקובץ לצפייה במחשב.
          </UnsupportedText>
          <PreviewDownloadButton onClick={() => onDownload(selectedDocument.id)}>
            <DownloadIcon /> הורד קובץ
          </PreviewDownloadButton>
        </UnsupportedPreview>
      );
    }

    if (!previewUrl) {
      return (
        <PreviewLoading>
          <span>שגיאה בטעינת התצוגה המקדימה</span>
        </PreviewLoading>
      );
    }

    if (previewInfo.type === 'pdf') {
      return <PreviewIframe src={previewUrl} title={selectedDocument.originalFileName} />;
    }

    if (previewInfo.type === 'image') {
      return <PreviewImage src={previewUrl} alt={selectedDocument.originalFileName} />;
    }

    return null;
  };


  return (
    <Container>
      <Header>
        <Title>מסמכים בתיקיה ({documents.length})</Title>
        <ActionButtons>
          <IconOnlyButton onClick={onRefresh} disabled={isLoading} title="רענון">
            <RefreshIcon />
          </IconOnlyButton>
          <IconOnlyButton onClick={handleUploadClick} disabled={isUploading} title="העלה מסמך">
            <UploadIcon />
          </IconOnlyButton>
        </ActionButtons>
      </Header>

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.xls,.xlsx"
        onChange={handleFileSelect}
      />

      {isUploading && (
        <UploadingOverlay>
          ⏳ מעלה מסמכים...
        </UploadingOverlay>
      )}

      {documents.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📂</EmptyIcon>
          <EmptyText>התיקיה ריקה</EmptyText>
          <ActionButton variant="primary" onClick={handleUploadClick}>
            העלה מסמך ראשון
          </ActionButton>
        </EmptyState>
      ) : (
        <DocumentsGrid>
          {documents.map(doc => (
            <DocumentCard
              key={doc.id}
              onClick={() => handleDocumentClick(doc)}
            >
              {doc.isNew && <NewBadge>חדש</NewBadge>}
              <DocumentIcon>{getFileIcon(doc.fileExtension)}</DocumentIcon>
              <DocumentName title={doc.originalFileName}>
                {doc.originalFileName}
              </DocumentName>
              <DocumentInfo>
                <span>{doc.formattedFileSize}</span>
                <span>{formatDate(doc.createdAt)}</span>
              </DocumentInfo>
              <DocumentActions>
                <CardIconButton onClick={(e) => { e.stopPropagation(); onDownload(doc.id); }} title="הורדה">
                  <DownloadIcon />
                </CardIconButton>
                {onConvertToPdf && isConvertibleTooPdf(doc.fileExtension) && (
                  <CardIconButton
                    onClick={(e) => handleConvertToPdfClick(doc, e)}
                    title="המר ל-PDF"
                    disabled={isConverting}
                  >
                    <ConvertToPdfIcon />
                  </CardIconButton>
                )}
                <CardIconButton danger onClick={(e) => handleDeleteClick(doc.id, e)} title="מחיקה">
                  <DeleteIcon />
                </CardIconButton>
              </DocumentActions>
            </DocumentCard>
          ))}
        </DocumentsGrid>
      )}

      {/* Preview Modal */}
      {selectedDocument && (
        <ModalOverlay>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {getFileIcon(selectedDocument.fileExtension)} {selectedDocument.originalFileName}
              </ModalTitle>
              <CloseButton onClick={handleClosePreview} title="סגור">
                ✕
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {renderPreviewContent()}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText || 'אישור'}
        isLoading={confirmModal.isLoading}
      />

      {/* Alert Modal */}
      <ConfirmationModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        confirmText="סגור"
        cancelText=""
      />
    </Container>
  );
};

export default FolderDocumentsGrid;
